import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useBaseUrl } from './BaseUrlContext';

const ProductsContext = createContext();

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [productBuffer, setProductBuffer] = useState([]); // Buffer for storing extra fetched products
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0); // Backend total pages
  const [selectedCompanyId, setSelectedCompanyId] = useState(1);
  const [appendMode, setAppendMode] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const { baseUrl, loading: baseUrlLoading } = useBaseUrl();

  const BACKEND_PAGE_SIZE = 50;
  const FRONTEND_CHUNK_SIZE = 15;

  const fetchProducts = async (append = false) => {
    if (!baseUrl) return;

    try {
      setLoading(true);
      setError(null);

      if (!append) {
        setProducts([]);
        setProductBuffer([]); // Clear buffer on fresh fetch
      }

      // Build URL with query parameters
      let url = `${baseUrl}Product/GetPaginatedProducts?pageNumber=${pageNumber}&pageSize=${BACKEND_PAGE_SIZE}`;
      if (selectedCompanyId) {
        url += `&Companyid=${selectedCompanyId}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const fetchedProducts = data.products || [];
      const total = data.totalProducts || 0;
      const pages = data.totalPages || 0; // Backend pages

      // Split logic: Take first chunk for display, rest for buffer
      const newDisplayChunk = fetchedProducts.slice(0, FRONTEND_CHUNK_SIZE);
      const newBuffer = fetchedProducts.slice(FRONTEND_CHUNK_SIZE);

      if (append) {
        setProducts(prev => [...prev, ...newDisplayChunk]);
      } else {
        setProducts(newDisplayChunk);
      }

      setProductBuffer(newBuffer);
      setTotalProducts(total);
      setTotalPages(pages);

      // Has more if buffer has items OR if backend has more pages
      setHasMore(newBuffer.length > 0 || pageNumber < pages);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset pagination when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      setPageNumber(1);
      setProductBuffer([]);
      setHasMore(true);
      setAppendMode(false);
    }
  }, [selectedCompanyId]);

  // Fetch products when dependencies change (only for BACKEND page changes)
  useEffect(() => {
    if (!baseUrlLoading && baseUrl) {
      fetchProducts(appendMode);
      if (appendMode) setAppendMode(false);
    }
  }, [pageNumber, selectedCompanyId, baseUrl, baseUrlLoading]);

  const loadMore = useCallback(() => {
    if (loading) return;

    // Phase 1: Check buffer first
    if (productBuffer.length > 0) {
      const nextChunk = productBuffer.slice(0, FRONTEND_CHUNK_SIZE);
      const remainingBuffer = productBuffer.slice(FRONTEND_CHUNK_SIZE);

      setProducts(prev => [...prev, ...nextChunk]);
      setProductBuffer(remainingBuffer);

      // Re-evaluate hasMore
      // Has more if remaining buffer > 0 OR if backend still has pages
      setHasMore(remainingBuffer.length > 0 || pageNumber < totalPages);
    }
    // Phase 2: If buffer empty, fetch next backend page
    else if (pageNumber < totalPages) {
      setAppendMode(true);
      setPageNumber(prev => prev + 1);
    }
  }, [loading, productBuffer, pageNumber, totalPages]);

  const value = useMemo(() => ({
    products,
    loading: loading || baseUrlLoading,
    error,
    pageNumber,
    totalProducts,
    setPageNumber,
    totalPages,
    pageSize: FRONTEND_CHUNK_SIZE, // Expose frontend size
    setSelectedCompanyId,
    selectedCompanyId,
    isSinglePage: totalProducts <= FRONTEND_CHUNK_SIZE,
    hasMore,
    loadMore,
    refetch: () => fetchProducts(false)
  }), [products, loading, baseUrlLoading, error, pageNumber, totalProducts,
    totalPages, selectedCompanyId, hasMore, loadMore]);

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};
